import React, { useMemo, useRef, useEffect } from "react";

// OptionStrat-style expiration date strip — visually compact.
//
// Layout per month group:
//   [Jun]   3 4 5 8 9 10 11 [12] 15 16 17 [18]
//   ^ month chip (dark blue pill, bold)
//                          ^ active day  ^ second active day
// Days are 28-px circle/squarish pills. Active = solid blue.
// "Leg-on-this-date" = a tiny purple bar under the day number.
//
// Props:
//   expirations: array of {date:'YYYY-MM-DD', dte:int}
//   selected:    array of YYYY-MM-DD strings (current focus + each leg's expiry)
//   legDates:    array of YYYY-MM-DD strings (one per leg, may repeat) — used for the purple under-bar
//   onPick:      (date) => void

const monthShort = (d) => d.toLocaleString("en-US", { month: "short" });

const ExpirationStrip = ({ expirations = [], selected = [], legDates = [], onPick }) => {
  const sel = useMemo(() => new Set(selected), [selected]);
  const legSet = useMemo(() => new Set(legDates), [legDates]);
  const ref = useRef(null);

  const days = useMemo(() => {
    if (!expirations.length) return [];
    const byDate = new Map(expirations.map((e) => [e.date, e]));
    const all = expirations.map((e) => new Date(e.date + "T00:00:00")).sort((a, b) => a - b);
    const last = all[all.length - 1];
    const maxDays = 120;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cursor = new Date(today);
    const out = [];
    for (let i = 0; i < maxDays && cursor <= last; i++) {
      const iso = cursor.toISOString().slice(0, 10);
      const exp = byDate.get(iso);
      out.push({
        iso,
        date: new Date(cursor),
        day: cursor.getDate(),
        month: cursor.getMonth(),
        year: cursor.getFullYear(),
        tradable: !!exp,
        dte: exp ? exp.dte : null,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return out;
  }, [expirations]);

  const monthGroups = useMemo(() => {
    const groups = [];
    let curr = null;
    days.forEach((d) => {
      if (!curr || curr.month !== d.month || curr.year !== d.year) {
        curr = { month: d.month, year: d.year, label: monthShort(d.date), days: [] };
        groups.push(curr);
      }
      curr.days.push(d);
    });
    return groups;
  }, [days]);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current.querySelector('[data-active="true"]')
      || ref.current.querySelector('[data-tradable="true"]');
    if (!el) return;
    // Only scroll the strip's own horizontal overflow, never the page.
    const container = ref.current;
    const offset = el.offsetLeft - container.clientWidth / 2 + el.clientWidth / 2;
    container.scrollTo({ left: Math.max(0, offset), behavior: "smooth" });
  }, [expirations.length, selected.join(",")]);

  if (!expirations.length) {
    return (
      <div
        className="bg-[#0E1A30] border border-white/10 rounded-xl px-4 py-2.5 text-[12px] text-white/45"
        data-testid="forge-expiration-strip-empty"
      >
        Loading expirations…
      </div>
    );
  }

  return (
    <div
      className="bg-[#0E1A30] border border-white/10 rounded-xl px-3 py-2"
      data-testid="forge-expiration-strip"
    >
      <div ref={ref} className="flex items-stretch gap-4 overflow-x-auto no-scrollbar">
        {monthGroups.map((grp, gi) => (
          <div key={`${grp.year}-${grp.month}-${gi}`} className="flex items-center gap-1.5">
            {/* Month chip — OptionStrat-style dark-blue pill */}
            <div className="flex items-center justify-center h-7 px-2.5 rounded-md bg-[#1D2D54] border border-[#3B82F6]/25">
              <span className="text-[10.5px] uppercase tracking-[0.18em] font-medium text-white/85 num">
                {grp.label}
              </span>
            </div>
            <div className="flex items-center gap-[3px]">
              {grp.days.map((d) => {
                const isActive = sel.has(d.iso);
                const hasLeg = legSet.has(d.iso);
                if (!d.tradable) {
                  return (
                    <div
                      key={d.iso}
                      className="flex items-center justify-center w-7 h-7 text-[11.5px] num text-white/22 cursor-default"
                      data-tradable="false"
                    >
                      {d.day}
                    </div>
                  );
                }
                return (
                  <button
                    key={d.iso}
                    type="button"
                    onClick={() => onPick && onPick(d.iso)}
                    data-tradable="true"
                    data-active={isActive ? "true" : "false"}
                    data-testid={`forge-exp-${d.iso}`}
                    title={`${d.iso} · ${d.dte}d`}
                    className={`relative flex items-center justify-center w-7 h-7 rounded-md text-[11.5px] num leading-none font-medium transition ${
                      isActive
                        ? "bg-[#3B82F6] text-white shadow-[0_0_0_1px_#60A5FA_inset]"
                        : "text-white/80 hover:bg-white/[0.08] hover:text-white"
                    }`}
                  >
                    {d.day}
                    {/* Purple under-bar when this date is one of the legs. Shown
                        even on the active day so the leg indicator never hides. */}
                    {hasLeg && (
                      <span
                        className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-3 h-[2px] rounded-full ${
                          isActive ? "bg-white/85" : "bg-[#A98CFF]"
                        }`}
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExpirationStrip;
